/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useContext, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import * as Sentry from '@sentry/react';
import type {
  LearnerProfile_Ethnicity as Ethnicity,
  LearnerProfile_GenderEnum as Gender,
  LearnerProfile_VeteranStatus as VeteranStatus,
} from '__generated__/graphql-types';
import type {
  LearnerProfile_CountryWorkEligibilities as CountryWorkEligibilities,
  LearnerProfile_EnumGenderEntry as EnumGenderEntry,
  LearnerProfile_UserGenderEntry as UserGenderEntry,
  LearnerProfile_UserLocation as UserLocation,
} from '__generated__/graphql-types-do-not-use';
import { isNil } from 'lodash';

import {
  Button,
  CheckboxCard,
  CheckboxGroup,
  ContextualHelp,
  Dialog,
  Divider,
  Grid,
  InlineNotification,
  MultiSelect,
  PageNotification,
  SelectField,
  SelectOption,
  TextField,
  Typography,
  Typography2,
} from '@coursera/cds-core';

import type { LearnerProfile as Profile } from 'bundles/account-profile/common/types';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import RequiredFieldLabel from 'bundles/account-profile/components/recruiting-profile-viewer/RequiredFieldLabel';
import LocationInput from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/LocationInput';
import ProfilePhotoUploader from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ProfilePhotoUploader';
import VeteranStatusInformation from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/VeteranStatusInformation';
import { validateFullName } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileUtils';
import type { ValidGenderOptionTypes } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/utils';
import {
  ETHNICITY_OPTIONS,
  GENDER_OPTIONS,
  REDUCED_GENDER_OPTIONS,
  VETERAN_STATUS_OPTIONS,
  convertEthnicityEnumToString,
  convertGenderEnumToString,
  convertVeteranStatusEnumToString,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/utils';
import {
  validateAll,
  validateField,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/validationUtils';
import type {
  ValidationErrors,
  ValidationRules,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/validationUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  formContainer: css`
    // Remove inherited styes that were conflicating with cds
    input {
      margin-bottom: 0;
      border: 0;
      box-shadow: none;
      background: transparent;
    }

    input[aria-invalid='true']:invalid:focus {
      box-shadow: none;
    }
  `,
  formErrors: () => css`
    margin-top: var(--cds-spacing-300);
  `,
  formInput: css`
    label {
      margin-bottom: 0;
    }
  `,
  checkboxGroup: css`
    width: 100%;
  `,
  multiSelect: () => css`
    button {
      margin-top: var(--cds-spacing-100);
      width: 100%;
      max-width: 100%;
    }
  `,
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type RequiredFields = 'fullName' | 'location';
type FormFieldRefs = Record<RequiredFields, MutableRefObject<HTMLDivElement | null>>;

const NAME_LENGTH_LIMIT = 128;
const PRONOUN_LENGTH_LIMIT = 25;

const E2E_SELECTORS = {
  PROFILE_SECTION_MODAL: 'profile-photo-and-invitation-dialog',
  INPUT_FULLNAME: 'profile_edit_fullName',
  INPUT_LOCATION: 'profile_edit_location',
  INPUT_PRONOUNS: 'profile_edit_pronouns',
  INPUT_GENDER: 'profile_edit_gender',
  INPUT_AUTHORIZED: 'profile_edit_authorized',
  INPUT_SPONSOR: 'profile_edit_sponsor',
} as const;

const EditProfileDialog = ({ isOpen, onClose }: Props) => {
  const { profileState, updateLearnerProfile, restrictHiringSolutions } = useContext(ProfileContext);
  const { showSensitivePersonalInfoFields } =
    (profileState && 'displaySettings' in profileState && profileState.displaySettings) || {};

  const learnerProfileGender =
    (profileState && 'learnerProfileGender' in profileState && profileState.learnerProfileGender) || undefined;
  const learnerProfileEthnicities = profileState && 'ethnicities' in profileState && profileState.ethnicities;
  const learnerProfileVeteranStatus = profileState && 'veteranStatus' in profileState && profileState.veteranStatus;
  const learnerProfileInferredUserLocationCountryCode =
    profileState && 'inferredUserLocationCountryCode' in profileState && profileState.inferredUserLocationCountryCode;

  const { HeadingGroup, Content, Actions } = Dialog;
  const [fullName, setFullName] = useState(profileState?.fullName);
  const [pronoun, setPronoun] = useState(profileState?.pronoun);
  const [gender, setGender] = useState(learnerProfileGender);
  const userGenderInput = (learnerProfileGender as UserGenderEntry)?.userGenderInput;
  const [showCustomGenderInput, setShowCustomGenderInput] = useState(!!userGenderInput || userGenderInput === ''); // Empty string is valid input
  const [location, setLocation] = useState<UserLocation | undefined>(profileState?.location || undefined);
  const [profilePhoto, setProfilePhoto] = useState(profileState?.profilePhoto);
  const [requiresUSVisaSponsorship, setRequiresUSVisaSponsorship] = useState<boolean | undefined>(
    isNil(profileState?.countryWorkEligibilities?.US?.requiresVisaSponsorship)
      ? undefined
      : Boolean(profileState?.countryWorkEligibilities?.US?.requiresVisaSponsorship)
  );
  const [hasUSWorkAuthorization, setHasUSWorkAuthorization] = useState<boolean | undefined>(
    isNil(profileState?.countryWorkEligibilities?.US?.hasWorkAuthorization)
      ? undefined
      : Boolean(profileState?.countryWorkEligibilities?.US?.hasWorkAuthorization)
  );
  const [ethnicities, setEthnicities] = useState<Array<Ethnicity>>(learnerProfileEthnicities || []);
  const [veteranStatus, setVeteranStatus] = useState<VeteranStatus | undefined>(
    learnerProfileVeteranStatus || undefined
  );
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});
  const showFullNameValidationWarning = !profileState?.fullName?.length && !Object.keys(formErrors).length;

  const formFieldRefs: FormFieldRefs = {
    fullName: useRef(null),
    location: useRef(null),
  };
  const enableUSFields = isNil(location?.countryName)
    ? learnerProfileInferredUserLocationCountryCode === 'US'
    : location?.countryName === 'United States';

  const validationRules = (): ValidationRules<Partial<Profile>, keyof Profile> => [
    {
      field: 'fullName',
      // @ts-expect-error URL: string
      getValidationErrors: (value?: string) => {
        if (!value) return _t('Please add first and last name');
        if (!validateFullName(value)) {
          return _t(
            'Your name contains numbers or invalid characters or only uppercase letters, the following characters are not allowed: _!@#$%^&*()+=?><[]{}|/'
          );
        }
        if (value.length > NAME_LENGTH_LIMIT) {
          return _t('Your name exceeds 128 characters.');
        }
        return '';
      },
    },
    {
      field: 'location',
      getValidationErrors: () => {
        return '';
      },
    },
    {
      field: 'pronoun',
      // @ts-expect-error URL: string
      getValidationErrors: (value?: string) => {
        if (!value) return '';
        if (value.length > PRONOUN_LENGTH_LIMIT) {
          return _t('Pronoun exceeds #{limit} characters.', { limit: PRONOUN_LENGTH_LIMIT });
        }
        return '';
      },
    },
    {
      field: 'countryWorkEligibilities',
      // @ts-expect-error URL: string
      getValidationErrors: (value?: CountryWorkEligibilities) => {
        const workAuthorizationValue = value?.US?.hasWorkAuthorization;
        const requiresVisaValue = value?.US?.requiresVisaSponsorship;

        if (enableUSFields && (!isNil(workAuthorizationValue) || !isNil(requiresVisaValue))) {
          if (isNil(workAuthorizationValue) && isNil(requiresVisaValue)) {
            return _t('Please select both your work authorization and employment visa status');
          }

          if (isNil(workAuthorizationValue)) {
            return _t('Please select your work authorization status');
          }

          if (isNil(requiresVisaValue)) {
            return _t('Please select your employment visa status');
          }
        }
        return '';
      },
    },
  ];

  const unsetDemographicFields = () => {
    setGender(undefined);
    setPronoun(undefined);
    setEthnicities([]);
    setVeteranStatus(undefined);
    setHasUSWorkAuthorization(undefined);
    setRequiresUSVisaSponsorship(undefined);
  };

  const resetFields = () => {
    setProfilePhoto(profileState?.profilePhoto);
    setFullName(profileState?.fullName);
    setPronoun(profileState?.pronoun);
    setGender(learnerProfileGender);
    setLocation(profileState?.location || undefined);
    setEthnicities(learnerProfileEthnicities || []);
    setVeteranStatus(learnerProfileVeteranStatus || undefined);
  };

  const validateTextField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event?.target;
    const validationMessage = validateField(validationRules, name as keyof Profile, value);
    const errors: ValidationErrors = {
      ...formErrors,
      [name]: validationMessage,
    };
    setFormErrors(errors);
  };

  const validateLocationField = (value: UserLocation) => {
    const validationMessage = validateField(validationRules, 'location' as keyof Profile, value);
    const errors: ValidationErrors = {
      ...formErrors,
      location: validationMessage,
    };
    setFormErrors(errors);
  };

  const handleClose = () => {
    setFormErrors({});
    resetFields();
    onClose();
  };

  const scrollToFirstError = (validationErrors: ValidationErrors) => {
    const firstError = Object.keys(validationErrors)[0];
    const firstErrorRef = formFieldRefs[firstError as RequiredFields];
    firstErrorRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    const hasLocationCountrySavedToUS = location?.countryName === 'United States';

    const updatedFields: Partial<Profile> = {
      profilePhoto,
      fullName,
      learnerProfileGender: gender,
      pronoun,
      location,
      countryWorkEligibilities: {
        US: {
          requiresVisaSponsorship: requiresUSVisaSponsorship,
          hasWorkAuthorization: hasUSWorkAuthorization,
        },
      },
      ethnicities: hasLocationCountrySavedToUS ? ethnicities ?? [] : [],
      veteranStatus: hasLocationCountrySavedToUS ? veteranStatus : undefined,
    };

    const validationErrors = validateAll(validationRules, updatedFields);
    setFormErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setIsSaving(false);
      scrollToFirstError(validationErrors);
      return;
    }

    try {
      await updateLearnerProfile(updatedFields);
      setIsSaving(false);
      onClose();
    } catch (error) {
      setIsSaving(false);
      setFormErrors((prevState) => ({
        ...prevState,
        form: _t('There was an error saving the form, please try again'),
      }));
      Sentry.captureException(error);
    }
  };

  const handleLocationUpdate = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    async (updatedLocation: UserLocation) => {
      setIsSaving(true);
      unsetDemographicFields();

      const updatedFields: Partial<Profile> = {
        location: updatedLocation,
        learnerProfileGender: undefined,
        pronoun: '',
        countryWorkEligibilities: {
          US: {
            requiresVisaSponsorship: undefined,
            hasWorkAuthorization: undefined,
          },
        },
        ethnicities: [],
        veteranStatus: undefined,
      };

      try {
        await updateLearnerProfile(updatedFields, false);
        setIsSaving(false);
      } catch {
        setIsSaving(false);
        setFormErrors((prevState) => ({
          ...prevState,
          form: _t('There was an error saving the form, please try again'),
        }));
      }
    },
    [updateLearnerProfile]
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      width="medium"
      variant="standard"
      aria-describedby="dialog-content"
      data-e2e={E2E_SELECTORS.PROFILE_SECTION_MODAL}
    >
      <HeadingGroup>{_t('Personal details')}</HeadingGroup>
      <Content id="dialog-content">
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          // not tracked in eventing v3
          trackingName="profile_intro_profile_dialog"
          requireFullyVisible={false}
        >
          <Grid css={styles.formContainer} container spacing={24}>
            <Grid item xs={12}>
              <Typography2 component="p">
                {_t('Add your personal details as you would like it to appear on your profile.')}
              </Typography2>
            </Grid>
            {showFullNameValidationWarning && (
              <Grid item xs={12}>
                <PageNotification
                  severity="warning"
                  title={_t(
                    'Your first and last name are required for your profile to be visible to recruiters using Coursera Hiring Solutions'
                  )}
                >
                  {_t(
                    'Coursera has partnered with industry-leading companies with open positions. Please complete the required fields in order to join.'
                  )}
                </PageNotification>
              </Grid>
            )}
            <Grid item xs={12}>
              <ProfilePhotoUploader value={profilePhoto} onChange={setProfilePhoto} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="fullName"
                label={_t('First and last name')}
                renderLabel={(labelComponent: React.ReactElement) => (
                  <RequiredFieldLabel labelComponent={labelComponent} disableRequiredGatingLogic={true} />
                )}
                placeholder={_t('Enter your first and last name')}
                data-e2e={E2E_SELECTORS.INPUT_FULLNAME}
                value={fullName || ''}
                validationStatus={formErrors.fullName ? 'error' : undefined}
                validationLabel={formErrors.fullName}
                type="text"
                inputProps={{
                  className: 'fs-mask',
                }}
                onChange={(e) => {
                  setFullName(e.target.value);
                  validateTextField(e);
                }}
                ref={formFieldRefs.fullName}
                css={styles.formInput}
              />
            </Grid>
            <Grid item xs={12}>
              <LocationInput
                value={location}
                onChange={(e) => {
                  validateLocationField(e);
                  if (e.countryName !== location?.countryName) {
                    handleLocationUpdate(e);
                  }
                  setLocation(e);
                }}
                validationStatus={formErrors.location ? 'error' : undefined}
                validationLabel={formErrors.location}
                ref={formFieldRefs.location}
                data-e2e={E2E_SELECTORS.INPUT_LOCATION}
              />
            </Grid>
            {showSensitivePersonalInfoFields && (
              <Grid item xs={12}>
                <TextField
                  optional={true}
                  fullWidth
                  name="pronoun"
                  label={_t('Pronouns')}
                  placeholder={_t('Enter your pronouns')}
                  value={pronoun || ''}
                  data-e2e={E2E_SELECTORS.INPUT_PRONOUNS}
                  validationStatus={formErrors.pronoun ? 'error' : undefined}
                  validationLabel={formErrors.pronoun}
                  onChange={(e) => {
                    setPronoun(e.target.value);
                    validateTextField(e);
                  }}
                  css={styles.formInput}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider color="dark" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h2semibold">{_t('Demographic Information')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <SelectField
                optional={true}
                fullWidth
                key={location?.countryCode ?? ''}
                label={_t('Gender')}
                placeholder={_t('Select gender')}
                data-e2e={E2E_SELECTORS.INPUT_GENDER}
                value={showCustomGenderInput ? 'GENDER_NOT_LISTED' : (gender as EnumGenderEntry)?.selectedEnum}
                onChange={(e) => {
                  if (e.target.value !== 'GENDER_NOT_LISTED') {
                    setShowCustomGenderInput(false);
                    setGender({
                      __typename: 'LearnerProfile_EnumGenderEntry',
                      selectedEnum: e.target.value as Gender,
                    });
                  } else {
                    setShowCustomGenderInput(true);
                    setGender({
                      __typename: 'LearnerProfile_UserGenderEntry',
                      userGenderInput: '',
                    });
                  }
                }}
                css={styles.formInput}
              >
                {(showSensitivePersonalInfoFields ? GENDER_OPTIONS : REDUCED_GENDER_OPTIONS).map(
                  (genderOption: ValidGenderOptionTypes) => (
                    <SelectOption
                      value={genderOption}
                      key={genderOption}
                      selected={
                        showCustomGenderInput
                          ? genderOption === 'GENDER_NOT_LISTED'
                          : (gender as EnumGenderEntry)?.selectedEnum === genderOption
                      }
                    >
                      {convertGenderEnumToString(genderOption)}
                    </SelectOption>
                  )
                )}
              </SelectField>
            </Grid>
            {showCustomGenderInput && (
              <Grid item xs={12}>
                <TextField
                  optional={true}
                  fullWidth
                  characterLimit={100}
                  name="customGender"
                  label={_t('Enter gender')}
                  placeholder={_t('E.G. Gender neutral')}
                  value={(gender as UserGenderEntry)?.userGenderInput || ''}
                  onChange={(e) => {
                    setGender({
                      __typename: 'LearnerProfile_UserGenderEntry',
                      userGenderInput: e.target.value,
                    });
                  }}
                  css={styles.formInput}
                />
              </Grid>
            )}
            {!restrictHiringSolutions && enableUSFields && (
              <React.Fragment>
                <Grid item xs={12} css={styles.multiSelect}>
                  <div>
                    <Typography2 component="span" variant="subtitleMedium">
                      {_t('Race/Ethnicities')}
                    </Typography2>
                  </div>
                  <MultiSelect
                    aria-label={_t('Race/Ethnicities')}
                    placeholder={
                      ethnicities.length === 0 ? _t('Select race/ethnicities') : _t('Selected race/ethnicities')
                    }
                    value={ethnicities}
                    onChange={(values) => {
                      setEthnicities(Array.from(values) as Array<Ethnicity>);
                    }}
                  >
                    {ETHNICITY_OPTIONS.map((ethnicityOption: Ethnicity) => (
                      <MultiSelect.Option key={ethnicityOption}>
                        {convertEthnicityEnumToString(ethnicityOption)}
                      </MultiSelect.Option>
                    ))}
                  </MultiSelect>
                </Grid>
                <Grid item xs={12}>
                  <SelectField
                    fullWidth
                    label={_t('Veteran Status')}
                    renderLabel={(labelComponent: React.ReactElement) => (
                      <ContextualHelp
                        helpToggleProps={{
                          'aria-label': 'Information about veteran status options',
                        }}
                        label={labelComponent}
                      >
                        <VeteranStatusInformation />
                      </ContextualHelp>
                    )}
                    placeholder={_t('Select veteran status')}
                    value={veteranStatus}
                    onChange={(e) => {
                      setVeteranStatus(e.target.value as VeteranStatus);
                    }}
                    css={styles.formInput}
                  >
                    {VETERAN_STATUS_OPTIONS.map((veteranStatusOption: VeteranStatus) => (
                      <SelectOption
                        value={veteranStatusOption}
                        key={veteranStatusOption}
                        selected={veteranStatusOption === veteranStatus}
                      >
                        {convertVeteranStatusEnumToString(veteranStatusOption)}
                      </SelectOption>
                    ))}
                  </SelectField>
                </Grid>
              </React.Fragment>
            )}
            <Grid item xs={12}>
              <PageNotification severity="information" title={_t('Why are we asking for this information?')}>
                {_t(
                  'This information will inform Coursera’s ongoing efforts to design an inclusive, learner-centered product.'
                )}
              </PageNotification>
            </Grid>
            {!restrictHiringSolutions && enableUSFields && (
              <>
                <Grid item xs={12}>
                  <Divider color="dark" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h2semibold" component="span">
                    {_t('Work eligibility')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <CheckboxGroup
                    label={_t('Are you authorized to work in the U.S.?')}
                    renderLabel={(labelComponent: React.ReactElement) => (
                      <RequiredFieldLabel labelComponent={labelComponent} />
                    )}
                    columns={2}
                    value={[...(isNil(hasUSWorkAuthorization) ? [] : [hasUSWorkAuthorization.toString()])]}
                    onChange={(e) => setHasUSWorkAuthorization(e.target.value === 'true')}
                    css={styles.checkboxGroup}
                    data-testid="authorized-to-work-in-us-checkbox"
                    data-e2e={E2E_SELECTORS.INPUT_AUTHORIZED}
                  >
                    {/* Checkbox values have to be a string */}
                    <CheckboxCard value="true" label={_t('Yes')} />
                    <CheckboxCard value="false" label={_t('No')} />
                  </CheckboxGroup>
                </Grid>
                <Grid item xs={12}>
                  <CheckboxGroup
                    label={_t('Will you now or in the future require sponsorship for an employment visa?')}
                    renderLabel={(labelComponent: React.ReactElement) => (
                      <RequiredFieldLabel labelComponent={labelComponent} />
                    )}
                    columns={2}
                    value={[...(isNil(requiresUSVisaSponsorship) ? [] : [requiresUSVisaSponsorship.toString()])]}
                    onChange={(e) => setRequiresUSVisaSponsorship(e.target.value === 'true')}
                    css={styles.checkboxGroup}
                    data-testid="need-employment-sponsorship-checkbox"
                    data-e2e={E2E_SELECTORS.INPUT_SPONSOR}
                  >
                    <CheckboxCard value="true" label={_t('Yes')} />
                    <CheckboxCard value="false" label={_t('No')} />
                  </CheckboxGroup>
                </Grid>
                {formErrors.countryWorkEligibilities && (
                  <Grid item xs={12}>
                    <InlineNotification severity="error">{formErrors.countryWorkEligibilities}</InlineNotification>
                  </Grid>
                )}
              </>
            )}
          </Grid>
          {formErrors.form && (
            <InlineNotification css={styles.formErrors} severity="error">
              {formErrors.form}
            </InlineNotification>
          )}
        </TrackedDiv>
      </Content>
      <Actions>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving}
          component={TrackedButton}
          // not tracked in eventing v3
          trackingName="profile_save_intro_profile"
          requireFullyVisible={false}
          withVisibilityTracking={true}
          loading={isSaving}
        >
          {_t('Save')}
        </Button>
      </Actions>
    </Dialog>
  );
};

const ErrorBoundEditProfileDialog = (props: Props) => (
  <ErrorBoundaryWithTagging
    componentName="EditProfileDialog"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the edit profile info dialog. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <EditProfileDialog {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundEditProfileDialog;
